import type { Attributes } from '@growthbook/growthbook';
import type { NextRouter } from 'next/router';
import globalState from '@/lib/globalState';

export type GrowthBookUserTargetingInput = {
  id?: string | number;
  isLoggedIn?: boolean;
  confirmedBookingsCount?: number;
  permissionsList?: string[];
  contactPermission?: unknown;
  hasBouncedEmail?: unknown;
  upcomingBookings?: unknown;
  pastBookings?: unknown;
  expiringCredit?: unknown;
  hasMarkedAsSpam?: unknown;
  last4?: unknown;
  accountBalances?: {
    edges?: Array<{
      node?: {
        amount?: number | null;
        currency?: string | null;
      } | null;
    } | null>;
  } | null;
};

export type GrowthBookAttributeInput = {
  siteId?: number;
  city: string;
  router: NextRouter;
  /** When true, include private user targeting attributes (post-consent only). */
  privateAttributesEnabled: boolean;
  /** Session-stable GrowthBook bucketing id; retained across consent changes to avoid rebucketing. */
  deviceId?: string;
  /** Amplitude identity included post-consent for warehouse identity joins, not bucketing. */
  amplitudeDeviceId?: string;
  /**
   * Viewport-derived layout tier for experiment targeting. Buckets align to the theme breakpoints the layout actually pivots on: mobile (<784px), tablet (784–1023px), desktop (>=1024px). Not a hardware/device detection.
   */
  viewportTier?: 'mobile' | 'tablet' | 'desktop';
  /** Explicit user targeting fields used for experiment targeting. */
  userTargeting?: GrowthBookUserTargetingInput;
};

const getSiteCountry = (): string | undefined =>
  globalState?.siteConfig?.countryCode ?? globalState?.siteConfig?.country ?? undefined;

// Missing count on a logged-in diner must be > 2 so the flag fails closed into control.
const UNKNOWN_LOGGED_IN_CONFIRMED_BOOKINGS_COUNT = 3;

function getConfirmedBookingsCountForTargeting(
  userTargeting?: GrowthBookUserTargetingInput
): number {
  const isLoggedIn =
    userTargeting?.isLoggedIn === true || (userTargeting?.id != null && userTargeting.id !== '');

  if (!isLoggedIn) {
    return 0;
  }

  return userTargeting?.confirmedBookingsCount ?? UNKNOWN_LOGGED_IN_CONFIRMED_BOOKINGS_COUNT;
}

/** Missing or non-positive balances fail closed to 0 so credit targeting stays out of the experiment. */
export function getAccountCreditAmount(
  user: Pick<GrowthBookUserTargetingInput, 'accountBalances'> | null | undefined
): number {
  const edges = user?.accountBalances?.edges;
  if (!edges?.length) {
    return 0;
  }

  const currency = globalState.siteConfig?.currency;
  const match =
    (currency ? edges.find((edge) => edge?.node?.currency === currency) : undefined) ?? edges[0];
  const amount = match?.node?.amount;

  return typeof amount === 'number' && amount > 0 ? amount : 0;
}

export const GROWTHBOOK_PRIVATE_ATTRIBUTE_KEYS = [
  'amplitude_device_id',
  'user_id',
  'logged_in',
  'viewport_tier',
  'coalesced_id',
  'contact_permission',
  'has_bounced_email',
  'has_upcoming_bookings',
  'has_past_bookings',
  'confirmed_bookings_count',
  'account_credit_amount',
  'has_expiring_credits',
  'has_marked_as_spam',
  'has_saved_card',
  'browser',
  'path_name',
  'as_path',
  'domain',
] as const;

/**
 * Attributes available before consent for client-side flag evaluation. Targeting-only values also stay on the private-key denylist above so anonymous ingestion never sends them.
 */
export const buildOperationalGrowthBookAttributes = ({
  siteId,
  city,
  deviceId,
  viewportTier,
  userTargeting,
}: Pick<
  GrowthBookAttributeInput,
  'siteId' | 'city' | 'deviceId' | 'viewportTier' | 'userTargeting'
>): Attributes => ({
  country: getSiteCountry(),
  city_name: city,
  site_id: siteId,
  ...(deviceId !== undefined && { device_id: deviceId }),
  viewport_tier: viewportTier,
  confirmed_bookings_count: getConfirmedBookingsCountForTargeting(userTargeting),
  account_credit_amount: getAccountCreditAmount(userTargeting),
  action_source: 'website',
  user_permissions: Array.isArray(userTargeting?.permissionsList)
    ? userTargeting.permissionsList
    : undefined,
  is_dev_mode: process.env.NODE_ENV !== 'production',
});

/** Full attribute set after consent. */
export const buildExperimentGrowthBookAttributes = ({
  siteId,
  city,
  router,
  deviceId: anonId,
  amplitudeDeviceId,
  viewportTier,
  userTargeting,
}: GrowthBookAttributeInput): Attributes => ({
  ...buildOperationalGrowthBookAttributes({
    siteId,
    city,
    deviceId: anonId,
    viewportTier,
    userTargeting,
  }),
  ...(amplitudeDeviceId !== undefined && { amplitude_device_id: amplitudeDeviceId }),
  user_id: userTargeting?.id ?? '',
  logged_in: userTargeting?.isLoggedIn ?? false,
  coalesced_id: userTargeting?.id ?? anonId,
  contact_permission: userTargeting?.contactPermission,
  has_bounced_email: userTargeting?.hasBouncedEmail,
  has_upcoming_bookings: !!userTargeting?.upcomingBookings,
  has_past_bookings: !!userTargeting?.pastBookings,
  has_expiring_credits: !!userTargeting?.expiringCredit,
  has_marked_as_spam: userTargeting?.hasMarkedAsSpam,
  has_saved_card: !!userTargeting?.last4,
  browser: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
  path_name: router.pathname,
  as_path: router.asPath,
  domain: typeof window !== 'undefined' ? new URL(window.location.href).hostname : undefined,
});

export const buildGrowthBookAttributes = (input: GrowthBookAttributeInput): Attributes =>
  input.privateAttributesEnabled
    ? buildExperimentGrowthBookAttributes(input)
    : buildOperationalGrowthBookAttributes(input);
